import React, { useContext, useEffect, useRef, useState } from "react";

import ItemGroup from "~/js/Views/Track/Statistics/StatisticsItemGroup";
import Flex from "Trackalyze/Flex";
import GPXData from "~/js/Views/Track/GPX/GPXData";
import { useTranslation } from "react-i18next";
import { MapGuiContext } from "~/Context/MapGuiContext";
import Button from "Trackalyze/Button";
import Card, { Cards } from "Trackalyze/Card";
import Typo from "Trackalyze/Typo";
import Surface from "Trackalyze/Surface";

const StatisticsContent = () => {
  const { t } = useTranslation();
  const { isReversed, toggleReverse, trackData } = useContext(MapGuiContext);
  const summaryRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowScrollTop(!entry.isIntersecting);
      },
      {
        root: document.getElementById("statistics-container"),
        threshold: 0,
      },
    );

    if (summaryRef.current) {
      observer.observe(summaryRef.current);
    }

    return () => {
      if (summaryRef.current) {
        observer.unobserve(summaryRef.current);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    const element = document.getElementById("statistics-container");
    if (element) {
      element.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const summaryItems = trackData
    ? [
        {
          key: "distance",
          condition: true,
          scrollTo: "distance",
          render: () => (
            <GPXData type="distance" id="total" variant="summary" />
          ),
        },
        {
          key: "steps",
          condition: trackData.stats.user?.steps > 0,
          scrollTo: "distance",
          render: () => (
            <GPXData
              type="steps"
              id="steps"
              value={trackData.stats.user.steps}
              variant="summary"
            />
          ),
        },
        {
          key: "elevation",
          condition: trackData.stats.elevation?.ascent > 0,
          scrollTo: "elevation",
          render: () => (
            <GPXData type="elevation" id="ascent" variant="summary" />
          ),
        },
        {
          key: "duration",
          condition: trackData.stats.duration?.total > 0,
          scrollTo: "duration",
          render: () => (
            <GPXData type="duration" id="total" variant="summary" />
          ),
        },
        {
          key: "speed",
          condition: trackData.stats.speed?.average > 0,
          scrollTo: "speed",
          render: () => <GPXData type="speed" id="average" variant="summary" />,
        },
        {
          key: "cadence",
          condition: trackData.stats.cadence?.average > 0,
          scrollTo: "cadence",
          render: () => (
            <GPXData type="cadence" id="average" variant="summary" />
          ),
        },
        {
          key: "heartrate",
          condition: trackData.stats.heartrate?.average > 0,
          scrollTo: "heartrate",
          render: () => (
            <GPXData type="heartrate" id="average" variant="summary" />
          ),
        },
        {
          key: "calories",
          condition: trackData.stats.user?.totalCalories > 0,
          scrollTo: "calories",
          render: () => (
            <GPXData
              type="calories"
              id="totalCalories"
              value={trackData.stats.user.totalCalories}
              variant="summary"
            />
          ),
        },
        {
          key: "temperature",
          condition: trackData.stats.temperature?.average > 0,
          scrollTo: "temperature",
          render: () => (
            <GPXData type="temperature" id="average" variant="summary" />
          ),
        },
      ]
    : [];

  const visibleSummaryItems = summaryItems.filter((item) => item.condition);

  return trackData !== null ? (
    <Flex
      vertical
      id="statistics-container"
      className="ta-shape_radius-m scroll-snap scroll-snap--y ta-is-overflow"
    >
      <Cards level="1">
        {/*  <Card className="ta-padding_s scroll-snap_item">
          <Flex spacing="3" className="ta-shape_radius-m scroll-snap_item">
            <ActivityIcon size="xs" activity={trackData.info.activity.symbol} />
            <Flex vertical flexitem>
              <Typo type="title" size="m" xsHigh>
                {trackData.info.name}
              </Typo>
              <Flex itemsCenter spacing="1">
                <Typo
                  inline
                  type="label"
                  size="s"
                  sHigh
                  className="ta-color-text_primary"
                  style={{ textTransform: "capitalize" }}
                >
                  {trackData.info.originalType}
                </Typo>
                <Typo
                  inline
                  type="label"
                  size="s"
                  sHigh
                  className="ta-color-text_on-surface-variant"
                >
                  <GPXData
                    value={trackData.stats.time.start}
                    type="timeDateLong"
                    id="timeDateStart"
                    variant="text"
                    noSymbol
                  />
                </Typo>
              </Flex>
            </Flex>
          </Flex>
        </Card> */}
        {isReversed && (
          <Surface
            outline
            shape="m"
            padding="xxxs"
            className="ta-margin_leftright-xs- scroll-snap_item"
          >
            <Flex itemscenter spacing="xs" out>
              <Typo
                size="m"
                className="ta-color-text_on-surface-variant ta-padding_xxs"
              >
                Track directions reversed. Statistics are calculated based on
                reversed track.
              </Typo>
              <Button text onClick={() => toggleReverse()}>
                Reset
              </Button>
            </Flex>
          </Surface>
        )}

        <div
          ref={summaryRef}
          className="ta-card ta-padding_xs statistics-summary-items scroll-snap_item"
        >
          {visibleSummaryItems.map((item) => (
            <div
              key={item.key}
              onClick={() => scrollToSection(item.scrollTo)}
              className="gpx_quickaction"
            >
              {item.render()}
            </div>
          ))}
        </div>

        {trackData.stats.duration.total > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="duration">
              <Flex vertical className="statistics-items">
                <GPXData
                  value={trackData.stats.time.start}
                  type="timeDateLong"
                  id="timeDateStart"
                  variant="list"
                  noSymbol
                />
                <GPXData
                  value={trackData.stats.time.end}
                  type="timeDateLong"
                  id="timeDateEnd"
                  variant="list"
                  noSymbol
                />
                <GPXData type="duration" variant="list" noSymbol />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        <Card className="scroll-snap_item">
          <ItemGroup id="distance">
            <Flex vertical className="statistics-items">
              <GPXData type="distance" id="total" variant="list" noSymbol />
              <GPXData
                type="steps"
                id="steps"
                value={trackData.stats.user.steps}
                variant="list"
                noSymbol
              />
            </Flex>
          </ItemGroup>
        </Card>
        {trackData.stats.elevation.ascent !== 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="elevation">
              <Flex vertical className="statistics-items">
                <GPXData type="elevation" variant="list" noSymbol />
              </Flex>
            </ItemGroup>
          </Card>
        )}

        {trackData.stats.duration.total > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="speed">
              <Flex vertical className="statistics-items">
                <GPXData type="speed" variant="list" />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        {trackData.stats.duration.total > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="pace">
              <Flex vertical className="statistics-items">
                <GPXData type="pace" variant="list" />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        {trackData.stats.cadence && trackData.stats.cadence.average > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="cadence">
              <Flex vertical className="statistics-items">
                <GPXData type="cadence" variant="list" />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        {trackData.stats.heartrate.average > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="heartrate">
              <Flex vertical className="statistics-items">
                <GPXData type="heartrate" variant="list" />
                <GPXData
                  type="heartRatePercent"
                  id="heartRatePercent"
                  value={trackData.stats.user.heartRatePercent}
                  variant="list"
                />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        {trackData.stats.user.totalCalories > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="calories">
              <Flex vertical className="statistics-items">
                <GPXData
                  type="calories"
                  id="totalCalories"
                  value={trackData.stats.user.totalCalories}
                  variant="list"
                />
                <GPXData
                  type="calories"
                  id="activeCalories"
                  value={trackData.stats.user.activeCalories}
                  variant="list"
                />
              </Flex>
            </ItemGroup>
          </Card>
        )}
        {trackData.stats.temperature.average > 0 && (
          <Card className="scroll-snap_item">
            <ItemGroup id="temperature">
              <Flex vertical className="statistics-items">
                <GPXData type="temperature" variant="list" />
              </Flex>
            </ItemGroup>
          </Card>
        )}
      </Cards>
      {showScrollTop && (
        <Button
          variant="tonal"
          icon
          symbol="arrow_upward"
          size="xs"
          onClick={scrollToTop}
          className="tracksdetails_upscroll animation_fade"
        />
      )}
    </Flex>
  ) : null;
};

export default StatisticsContent;
